import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const FONT_NAME = "Times New Roman";

const COLORS = {
  title: "FF1F4E79",
  headerFill: "FFD9EAF7",
  headerText: "FF1F2937",
  border: "FFB7C9D6",
  parentFill: "FFF3F6FA",
  totalBeforeFill: "FFE3F2FD",
  totalAfterFill: "FFE8F5E9",
  white: "FFFFFFFF",
  gray: "FF9E9E9E",
  green: "FF4CAF50",
  lightGreen: "FF8BC34A",
  blue: "FF2196F3",
  orange: "FFFF9800",
  red: "FFF44336",
  purple: "FF9C27B0",
};

const COLUMNS = [
  { key: "itemCode", header: "STT", width: 12 },
  { key: "itemName", header: "Hạng mục công việc", width: 42 },
  { key: "unit", header: "ĐVT", width: 10 },
  { key: "difficultyLevel", header: "Khó khăn", width: 12 },
  { key: "contractQuantity", header: "Khối lượng", width: 14 },
  { key: "unitPrice", header: "Đơn giá", width: 16 },
  { key: "contractAmount", header: "Thành tiền", width: 18 },
  { key: "priority", header: "Độ ưu tiên", width: 14 },
  { key: "status", header: "Trạng thái", width: 18 },
  { key: "startDate", header: "Từ ngày", width: 14 },
  { key: "endDate", header: "Đến ngày", width: 14 },
  { key: "completionPercentage", header: "Tiến độ", width: 12 },
  // { key: "paymentSummary", header: "Thanh toán / Nghiệm thu", width: 28 },
];

const getPaymentColumns = (paymentProfiles = []) =>
  (paymentProfiles || []).flatMap((profile, index) => [
    {
      key: `payment_quantity_${profile.paymentProfileId}`,
      header: "Khối lượng",
      groupHeader: `Nghiệm thu đợt ${index + 1}${profile.paymentDate ? ` - ${formatDate(profile.paymentDate)}` : ""
        }`,
      paymentProfileId: profile.paymentProfileId,
      paymentColumnType: "quantity",
      width: 14,
    },
    {
      key: `payment_amount_${profile.paymentProfileId}`,
      header: "Thành tiền",
      groupHeader: `Nghiệm thu đợt ${index + 1}${profile.paymentDate ? ` - ${formatDate(profile.paymentDate)}` : ""
        }`,
      paymentProfileId: profile.paymentProfileId,
      paymentColumnType: "amount",
      width: 18,
    },
  ]);

const getCumulativePaymentColumns = (paymentProfiles = []) => {
  if (!paymentProfiles || paymentProfiles.length === 0) return [];

  return [
    {
      key: "cumulative_quantity",
      header: "Khối lượng",
      groupHeader: "Lũy kế nghiệm thu",
      paymentColumnType: "cumulativeQuantity",
      width: 14,
    },
    {
      key: "cumulative_amount",
      header: "Thành tiền",
      groupHeader: "Lũy kế nghiệm thu",
      paymentColumnType: "cumulativeAmount",
      width: 18,
    },
  ];
};

const border = {
  top: { style: "thin", color: { argb: COLORS.border } },
  left: { style: "thin", color: { argb: COLORS.border } },
  bottom: { style: "thin", color: { argb: COLORS.border } },
  right: { style: "thin", color: { argb: COLORS.border } },
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getDate()).padStart(2, "0")}/${String(
    date.getMonth() + 1,
  ).padStart(2, "0")}/${date.getFullYear()}`;
};

const formatNumber = (value, maximumFractionDigits = 4) => {
  if (value === null || value === undefined || value === "") return "";
  let normalizedValue = value;
  if (typeof value === "string") {
    const rawValue = value.trim();
    if (rawValue.includes(".") && rawValue.includes(",")) {
      normalizedValue = rawValue.replace(/\./g, "").replace(",", ".");
    } else if (rawValue.includes(",")) {
      normalizedValue = rawValue.replace(",", ".");
    } else if (/^\d{1,3}(\.\d{3})+$/.test(rawValue)) {
      normalizedValue = rawValue.replace(/\./g, "");
    } else {
      normalizedValue = rawValue;
    }
  }
  const numberValue = Number(normalizedValue);
  if (!Number.isFinite(numberValue)) return "";
  return new Intl.NumberFormat("vi-VN", {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(numberValue);
};

const formatMoney = (value) => formatNumber(value, 0);

const formatQuantity = (value) => formatNumber(value, 2);

const safeFileName = (value) =>
  (value || "hang-muc-hop-dong")
    .toString()
    .trim()
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, "-")
    .slice(0, 120);

const getDifficultyColor = (level) => {
  switch (Number(level)) {
    case 1:
      return COLORS.green;
    case 2:
      return COLORS.lightGreen;
    case 3:
      return COLORS.orange;
    case 4:
      return COLORS.red;
    case 5:
      return COLORS.purple;
    default:
      return COLORS.gray;
  }
};

const getPriorityColor = (priority) => {
  const normalized = priority ? priority.toString().toLowerCase().trim() : "";
  const colors = {
    "cao": COLORS.red,
    "trung bình": COLORS.orange,
    "thấp": COLORS.green,
  };
  return colors[normalized] || COLORS.gray;
  // switch (Number(priority)) {
  //   case 1:
  //     return "#f44336"; // Đỏ - Cao
  //   case 2:
  //     return "#ff9800"; // Cam - Trung bình
  //   case 3:
  //     return "#4caf50"; // Xanh lá - Thấp
  //   default:
  //     return "#9e9e9e"; // Xám
  // }
};

const getStatusColor = (status) => {
  const normalized = status ? status.toString().toLowerCase().trim() : "";
  const colors = {
    "chưa bắt đầu": COLORS.gray,
    "đang thực hiện": COLORS.green,
    "hoàn thành": COLORS.blue,
    "tạm dừng": COLORS.orange,
    "hủy bỏ": COLORS.red,
  };
  return colors[normalized] || COLORS.gray;
};

const getPriorityLabel = (priority) => {
  switch (Number(priority)) {
    case 1:
      return "Cao";
    case 2:
      return "Trung bình";
    case 3:
      return "Thấp";
    default:
      return "";
  }
};

const isParentItem = (item) =>
  Boolean(item?.hasChildren || (item?.children && item.children.length > 0));

const flattenItems = (items, level = 0) =>
  (items || []).flatMap((item) => [
    { ...item, level_: level, hasChildren: isParentItem(item) },
    ...flattenItems(item.children || [], level + 1),
  ]);

const getLeafDescendants = (item) => {
  if (!item?.children || item.children.length === 0) return [item];
  return item.children.flatMap((child) => getLeafDescendants(child));
};

// const getPaymentSummary = (item, contractPayments = []) => {
//   const relatedItems = isParentItem(item) ? getLeafDescendants(item) : [item];
//   const itemIds = new Set(relatedItems.map((relatedItem) => relatedItem.itemId));
//   const relatedPayments = contractPayments.filter((payment) =>
//     itemIds.has(payment.contractItemId),
//   );

//   if (relatedPayments.length === 0) {
//     return { text: "", beforeTax: 0, progress: 0 };
//   }

//   const itemById = new Map(
//     relatedItems.map((relatedItem) => [relatedItem.itemId, relatedItem]),
//   );
//   const beforeTax = relatedPayments.reduce((total, payment) => {
//     const sourceItem = itemById.get(payment.contractItemId);
//     const amount =
//       payment.paymentAmount && payment.paymentAmount > 0
//         ? Math.round(payment.paymentAmount)
//         : Math.round(
//           (sourceItem?.unitPrice || 0) * (payment.paymentQuantity || 0),
//         );
//     return total + amount;
//   }, 0);
//   const progress =
//     item.contractAmount && item.contractAmount > 0
//       ? Math.min((beforeTax / item.contractAmount) * 100, 100)
//       : 0;

//   return {
//     text: `${relatedPayments.length} đợt - ${formatMoney(beforeTax)} - ${Math.round(
//       progress,
//     )}%`,
//     beforeTax,
//     progress,
//   };
// };

const getPaymentAmount = (sourceItem, payment) =>
  payment.paymentAmount && payment.paymentAmount > 0
    ? Math.round(payment.paymentAmount)
    : Math.round((sourceItem?.unitPrice || 0) * (payment.paymentQuantity || 0));

const getPaymentValue = (item, profileId, type, contractPayments = []) => {
  if (!profileId) return "";

  if (isParentItem(item)) {
    if (type === "quantity") return "";

    const leafItems = getLeafDescendants(item);
    const leafById = new Map(
      leafItems.map((leafItem) => [leafItem.itemId, leafItem]),
    );
    const totalAmount = contractPayments
      .filter(
        (payment) =>
          payment.paymentProfileId === profileId && leafById.has(payment.contractItemId),
      )
      .reduce(
        (total, payment) =>
          total + getPaymentAmount(leafById.get(payment.contractItemId), payment),
        0,
      );

    return totalAmount ? formatMoney(totalAmount) : "";
  }

  const payment = contractPayments.find(
    (itemPayment) =>
      itemPayment.contractItemId === item.itemId &&
      itemPayment.paymentProfileId === profileId,
  );

  if (!payment) return "";

  if (type === "quantity") {
    return formatQuantity(payment.paymentQuantity);
  }

  const paymentAmount = getPaymentAmount(item, payment);
  return paymentAmount ? formatMoney(paymentAmount) : "";
};

const getCumulativePaymentValue = (item, type, contractPayments = []) => {
  if (isParentItem(item)) {
    if (type === "cumulativeQuantity") return "";

    const leafItems = getLeafDescendants(item);
    const leafById = new Map(
      leafItems.map((leafItem) => [leafItem.itemId, leafItem]),
    );
    const totalAmount = contractPayments
      .filter((payment) => leafById.has(payment.contractItemId))
      .reduce(
        (total, payment) =>
          total + getPaymentAmount(leafById.get(payment.contractItemId), payment),
        0,
      );

    return totalAmount ? formatMoney(totalAmount) : "";
  }

  const itemPayments = contractPayments.filter(
    (payment) => payment.contractItemId === item.itemId,
  );

  if (type === "cumulativeQuantity") {
    const totalQuantity = itemPayments.reduce(
      (total, payment) => total + (Number(payment.paymentQuantity) || 0),
      0,
    );

    return totalQuantity ? formatQuantity(totalQuantity) : "";
  }

  const totalAmount = itemPayments.reduce(
    (total, payment) => total + getPaymentAmount(item, payment),
    0,
  );

  return totalAmount ? formatMoney(totalAmount) : "";
};

const getPaymentProfileTotal = (profileId, contractPayments = [], items = []) => {
  const leafItems = flattenItems(items).filter((item) => !isParentItem(item));
  const itemById = new Map(leafItems.map((item) => [item.itemId, item]));

  return contractPayments
    .filter((payment) => payment.paymentProfileId === profileId)
    .reduce(
      (total, payment) =>
        total + getPaymentAmount(itemById.get(payment.contractItemId), payment),
      0,
    );
};

const getCumulativePaymentTotal = (contractPayments = [], items = []) => {
  const leafItems = flattenItems(items).filter((item) => !isParentItem(item));
  const itemById = new Map(leafItems.map((item) => [item.itemId, item]));

  return contractPayments.reduce(
    (total, payment) =>
      total + getPaymentAmount(itemById.get(payment.contractItemId), payment),
    0,
  );
};

const applyCellBaseStyle = (cell) => {
  cell.border = border;
  cell.alignment = {
    vertical: "middle",
    horizontal: "left",
    wrapText: true,
  };
};

const addInfoRow = (worksheet, label, value, totalColumns = COLUMNS.length) => {
  const row = worksheet.addRow([label?.toUpperCase(), "", value ?? ""]);
  worksheet.mergeCells(row.number, 1, row.number, 2);
  worksheet.mergeCells(row.number, 3, row.number, totalColumns);
  row.getCell(1).font = { bold: true, name: `${FONT_NAME}` };
  row.getCell(1).alignment = { horizontal: "right" };
  row.getCell(3).font = { name: `${FONT_NAME}` };
  row.getCell(3).alignment = { horizontal: "left", wrapText: true };
  return row;
};

const styleDataRow = (row, rowData, totalColumns = COLUMNS.length) => {
  row.eachCell({ includeEmpty: true }, applyCellBaseStyle);

  // if (rowData.isParent) {
  if (rowData.level === 0) {
    row.font = { bold: true, name: `${FONT_NAME}` };
    row.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: COLORS.parentFill },
      };
    });
  } else {
    row.font = {
      bold: false,
      name: FONT_NAME,
    };
  }

  // row.getCell(1).font = { name: `${FONT_NAME}` };
  row.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
  row.getCell(2).alignment = {
    horizontal: "left",
    vertical: "middle",
    wrapText: true,
    indent: Math.min(rowData.level || 0, 8),
  };
  [3, 4, 8, 9, 12].forEach((index) => {
    row.getCell(index).alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
  });
  [5, 6, 7].forEach((index) => {
    row.getCell(index).alignment = {
      horizontal: "right",
      vertical: "middle",
      wrapText: true,
    };
  });
  [10, 11].forEach((index) => {
    row.getCell(index).alignment = { horizontal: "center", vertical: "middle" };
  });
  for (let index = COLUMNS.length + 1; index <= totalColumns; index += 1) {
    row.getCell(index).alignment = {
      horizontal: "right",
      vertical: "middle",
      wrapText: true,
    };
  }

  if (rowData.difficultyLevel) {
    row.getCell(4).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: getDifficultyColor(rowData.difficultyLevel) },
    };
    row.getCell(4).font = { bold: true, color: { argb: COLORS.white }, name: `${FONT_NAME}` };
  }

  if (rowData.priority) {
    row.getCell(8).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: getPriorityColor(rowData.priority) },
    };
    row.getCell(8).font = { bold: true, color: { argb: COLORS.white }, name: `${FONT_NAME}` };
  }

  if (rowData.status) {
    row.getCell(9).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: getStatusColor(rowData.status) },
    };
    row.getCell(9).font = { bold: true, color: { argb: COLORS.white }, name: `${FONT_NAME}` };
  }
};

const addSummaryRow = (
  worksheet,
  label,
  amount,
  fillColor,
  exportColumns = COLUMNS,
  paymentProfileTotals = {},
  cumulativeTotals = {},
) => {
  const rowValues = exportColumns.map((column) => {
    if (column.key === "itemCode") return "TỔNG";
    if (column.key === "itemName") return label;
    if (column.key === "contractAmount") {
      return amount !== null && amount !== undefined && amount !== ""
        ? formatMoney(amount)
        : "";
    }
    if (column.paymentColumnType === "amount") {
      const total = paymentProfileTotals[column.paymentProfileId];
      return total !== null && total !== undefined && total !== ""
        ? formatMoney(total)
        : "";
    }
    if (column.paymentColumnType === "cumulativeAmount") {
      const total = cumulativeTotals?.cumulativeAmount;
      return total !== null && total !== undefined && total !== ""
        ? formatMoney(total)
        : "";
    }
    return "";
  });
  const row = worksheet.addRow(rowValues);
  row.eachCell({ includeEmpty: true }, applyCellBaseStyle);
  row.font = { bold: true, name: `${FONT_NAME}` };
  row.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: fillColor },
    };
  });
  row.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
  row.getCell(2).alignment = { horizontal: "left", vertical: "middle" };
  row.getCell(7).alignment = { horizontal: "right", vertical: "middle" };
  row.getCell(7).numFmt = "#,##0";
  for (let index = COLUMNS.length + 1; index <= exportColumns.length; index += 1) {
    row.getCell(index).alignment = { horizontal: "right", vertical: "middle" };
    row.getCell(index).numFmt = "#,##0";
  }
};

export const exportContractItemsExcel = async ({
  items = [],
  contract = {},
  paymentProfiles = [],
  contractPayments = [],
  summaryTotals = {},
  exportedBy = "",
} = {}) => {
  const paymentColumns = getPaymentColumns(paymentProfiles);
  const cumulativePaymentColumns = getCumulativePaymentColumns(paymentProfiles);
  const exportColumns = [...COLUMNS, ...paymentColumns, ...cumulativePaymentColumns];
  const totalColumns = exportColumns.length;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = exportedBy || "Project Trak";
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet("Hạng mục hợp đồng", {
    views: [{ state: "frozen", ySplit: 10 }],
  });

  worksheet.columns = exportColumns.map((column) => ({
    key: column.key,
    width: column.width,
  }));

  worksheet.mergeCells(1, 1, 1, totalColumns);
  const titleCell = worksheet.getCell(1, 1);
  titleCell.value = "HẠNG MỤC HỢP ĐỒNG";
  titleCell.font = { bold: true, size: 16, color: { argb: COLORS.title }, name: `${FONT_NAME}` };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };
  worksheet.getRow(1).height = 28;

  const contractName =
    contract?.contractName || contract?.name || contract?.title || "";
  const contractCode =
    contract?.contractNumber || contract?.contractCode || contract?.code || "";
  const customerName =
    contract?.clientName ||
    contract?.customerName ||
    contract?.investorName ||
    contract?.ownerName ||
    "";

  addInfoRow(worksheet, "Tên hợp đồng: ", contractName, totalColumns);
  addInfoRow(worksheet, "Mã hợp đồng: ", contractCode, totalColumns);
  addInfoRow(worksheet, "Chủ đầu tư / khách hàng: ", customerName, totalColumns);
  addInfoRow(worksheet, "Ngày xuất file: ", formatDate(new Date()), totalColumns);
  addInfoRow(worksheet, "Người xuất: ", exportedBy, totalColumns);
  addInfoRow(
    worksheet,
    "Tổng giá trị trước thuế: ",
    formatMoney(
      summaryTotals.totalBeforeTaxRounded ?? summaryTotals.totalBeforeTax,
    ),
    totalColumns,
  );
  addInfoRow(
    worksheet,
    "Tổng giá trị sau thuế",
    formatMoney(
      summaryTotals.totalAfterTaxRoundedForSummary ??
      summaryTotals.totalAfterTaxRounded ??
      summaryTotals.totalAfterTax,
    ),
    totalColumns,
  );
  worksheet.addRow([]);

  const hasPaymentColumns = paymentColumns.length > 0 || cumulativePaymentColumns.length > 0;
  const headerRow = worksheet.addRow(
    exportColumns.map((column) => column.groupHeader || column.header),
  );
  headerRow.height = 24;
  headerRow.eachCell((cell) => {
    applyCellBaseStyle(cell);
    cell.font = { bold: true, color: { argb: COLORS.headerText }, name: `${FONT_NAME}` };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: COLORS.headerFill },
    };
  });

  let subHeaderRow = null;
  if (hasPaymentColumns) {
    subHeaderRow = worksheet.addRow(
      exportColumns.map((column) => (column.groupHeader ? column.header : "")),
    );
    subHeaderRow.height = 24;
    subHeaderRow.eachCell({ includeEmpty: true }, (cell) => {
      applyCellBaseStyle(cell);
      cell.font = { bold: true, color: { argb: COLORS.headerText }, name: `${FONT_NAME}` };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: COLORS.headerFill },
      };
    });

    for (let index = 1; index <= COLUMNS.length; index += 1) {
      worksheet.mergeCells(headerRow.number, index, subHeaderRow.number, index);
    }

    paymentProfiles.forEach((profile) => {
      const firstColumnIndex =
        COLUMNS.length +
        paymentProfiles.findIndex(
          (item) => item.paymentProfileId === profile.paymentProfileId,
        ) *
        2 +
        1;
      worksheet.mergeCells(
        headerRow.number,
        firstColumnIndex,
        headerRow.number,
        firstColumnIndex + 1,
      );
    });

    if (cumulativePaymentColumns.length > 0) {
      const firstCumulativeColumnIndex =
        COLUMNS.length + paymentColumns.length + 1;
      worksheet.mergeCells(
        headerRow.number,
        firstCumulativeColumnIndex,
        headerRow.number,
        firstCumulativeColumnIndex + cumulativePaymentColumns.length - 1,
      );
    }
  }

  worksheet.views = [
    { state: "frozen", ySplit: hasPaymentColumns ? subHeaderRow.number : headerRow.number },
  ];

  flattenItems(items).forEach((item) => {
    // const paymentSummary = getPaymentSummary(item, contractPayments);
    const rowData = {
      itemCode: item.itemCode || "",
      itemName: item.itemName || "",
      unit: item.unit || "",
      difficultyLevel: isParentItem(item) ? "" : item.difficultyLevel || "",
      contractQuantity: isParentItem(item)
        ? ""
        : formatQuantity(item.contractQuantity),
      unitPrice: isParentItem(item) ? "" : formatMoney(item.unitPrice),
      contractAmount: formatMoney(item.contractAmount),
      priority: isParentItem(item) ? "" : getPriorityLabel(item.priority),
      status: isParentItem(item) ? "" : item.status || "",
      startDate: isParentItem(item) ? "" : formatDate(item.startDate),
      endDate: isParentItem(item) ? "" : formatDate(item.endDate),
      completionPercentage:
        isParentItem(item) ||
          item.completionPercentage === null ||
          item.completionPercentage === undefined
          ? ""
          : `${Math.round(Number(item.completionPercentage) || 0)}%`,
      // paymentSummary: paymentSummary.text,
      level: item.level_,
      isParent: isParentItem(item),
    };
    paymentColumns.forEach((column) => {
      rowData[column.key] = getPaymentValue(
        item,
        column.paymentProfileId,
        column.paymentColumnType,
        contractPayments,
      );
    });
    cumulativePaymentColumns.forEach((column) => {
      rowData[column.key] = getCumulativePaymentValue(
        item,
        column.paymentColumnType,
        contractPayments,
      );
    });

    const row = worksheet.addRow(exportColumns.map((column) => rowData[column.key]));
    styleDataRow(row, rowData, totalColumns);
    row.getCell(6).numFmt = "#,##0";
    row.getCell(7).numFmt = "#,##0";
    paymentColumns.forEach((column, index) => {
      const cell = row.getCell(COLUMNS.length + index + 1);
      if (column.paymentColumnType === "amount") {
        cell.numFmt = "#,##0";
      }
    });
    cumulativePaymentColumns.forEach((column, index) => {
      const cell = row.getCell(
        COLUMNS.length + paymentColumns.length + index + 1,
      );
      if (column.paymentColumnType === "cumulativeAmount") {
        cell.numFmt = "#,##0";
      }
    });
  });

  if (items.length > 0) {
    const paymentProfileTotals = Object.fromEntries(
      paymentProfiles.map((profile) => [
        profile.paymentProfileId,
        getPaymentProfileTotal(profile.paymentProfileId, contractPayments, items),
      ]),
    );
    const afterTaxPaymentProfileTotals = Object.fromEntries(
      paymentProfiles.map((profile) => {
        const beforeTaxAmount = paymentProfileTotals[profile.paymentProfileId] || 0;
        const vatRate = (profile.vat || contract?.vat || 8) / 100;
        return [
          profile.paymentProfileId,
          Math.round(beforeTaxAmount * (1 + vatRate)),
        ];
      }),
    );
    const cumulativePaymentTotal = getCumulativePaymentTotal(
      contractPayments,
      items,
    );
    const afterTaxCumulativePaymentTotal = paymentProfiles.reduce(
      (total, profile) => {
        const beforeTaxAmount = paymentProfileTotals[profile.paymentProfileId] || 0;
        const vatRate = (profile.vat || contract?.vat || 8) / 100;
        return total + Math.round(beforeTaxAmount * (1 + vatRate));
      },
      0,
    );
    addSummaryRow(
      worksheet,
      "(trước thuế)",
      summaryTotals.totalBeforeTaxRounded ?? summaryTotals.totalBeforeTax,
      COLORS.totalBeforeFill,
      exportColumns,
      paymentProfileTotals,
      { cumulativeAmount: cumulativePaymentTotal },
    );
    addSummaryRow(
      worksheet,
      "(sau thuế)",
      summaryTotals.totalAfterTaxRoundedForSummary ??
      summaryTotals.totalAfterTaxRounded ??
      summaryTotals.totalAfterTax,
      COLORS.totalAfterFill,
      exportColumns,
      afterTaxPaymentProfileTotals,
      { cumulativeAmount: afterTaxCumulativePaymentTotal },
    );
  }

  worksheet.autoFilter = {
    from: { row: headerRow.number, column: 1 },
    to: { row: hasPaymentColumns ? subHeaderRow.number : headerRow.number, column: totalColumns },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const suffix = new Date().toISOString().slice(0, 10);
  saveAs(blob, `${safeFileName(contractCode || contractName)}-${suffix}.xlsx`);
};

export default exportContractItemsExcel;
